
import { Column } from "primereact/column";
import MeaLink from "../../../../components/MeaLink";
import useUrlState from "@ahooksjs/use-url-state";
import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useRef,
} from "react";
import MeaDataTable from "../../../../components/MeaDataTable";
import levelsAPI from "../../../../services/levels";
import contractsAPI from "../../../../services/contracts";
import MeaStudentAvatar from "../../../trainings/components/MeaStudentAvatar";
import MeaMemberAvatar from "../../../../components/MeaMemberAvatar";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import MeaButton from "../../../../components/MeaButton";
import MeaContractDueBalance from "../../../registrations/components/MeaContractDueBalance";
import MeaContractTotalAmount from "../../../registrations/components/MeaContractTotalAmount";
import { PERMISSION_TRAINING_CONTRACTS, PERMISSION_SCHOOL_MEMBERS, PERMISSION_TRAINING_STUDENTS } from "../../../../permissions";
import { useAuthContext } from "../../../../hooks/useAuthContext";
import { BlockUI } from "primereact/blockui";

const PAYMENT_FILTER_OPTIONS = [
  { label: "Tous les contrats", value: "" },
  { label: "Non entièrement payés", value: "true" },
  { label: "Totalement payés", value: "fully_paid" },
];

const ContractsListComponent = forwardRef((props, ref) => {
  const { selectedFields, filter, showAddButton, showToolbar, isLoading, getContractLink } = props;
  const schoolId = props?.schoolId;
  const trainingId = props?.trainingId;
  const [page, setPage] = useState();
  const [pageSize, setPageSize] = useState();
  const [searchInputValue, setSearchInputValue] = useState("");
  const { hasPermission } = useAuthContext();
  const accessToken = "";
  const searchInputRef = useRef();
  //
  //
  let selectedData = {
    id: selectedFields?.id ?? true,
    name: selectedFields?.name ?? true,
    students: selectedFields?.students ?? true,
    parents: selectedFields?.parents ?? true,
    total_students: selectedFields?.total_students ?? true,
    due_balance: selectedFields?.due_balance ?? true,
    total_amount: selectedFields?.total_amount ?? true,
    edit: selectedFields?.edit ?? true,
  };
  //
  const [state, setState] = useUrlState({
    contractsPage: "1",
    contractsPageSize: "30",
    contractsFilter: JSON.stringify({
      student_id: filter?.student_id || "",
      name: filter?.name || "",
      not_fully_paid: filter?.not_fully_paid ?? "",
    }),
  });
  //
  const contractsFilterParsed = JSON.parse(state.contractsFilter);
  const contractsFilterForQuery = {
    ...contractsFilterParsed,
    not_fully_paid:
      contractsFilterParsed.not_fully_paid === "true"
        ? "true"
        : contractsFilterParsed.not_fully_paid === "fully_paid"
        ? "fully_paid"
        : "",
  };

  const queryList = contractsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    page,
    pageSize,
    contractsFilterForQuery
  );
  //
  const levelsQuery = levelsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000
  );
  //
  const setFilter = (field, value) => {
    let currentFilter = state?.contractsFilter;
    let currentFilterObj = JSON.parse(currentFilter);
    currentFilterObj[field] = value;
    setState((s) => ({
      contractsPage: "1",
      contractsFilter: JSON.stringify(currentFilterObj),
    }));
  };

  const setSearchName = (name) => {
    setFilter("name", name || "");
  };

  const handleSearch = () => {
    setSearchName(searchInputValue);
  };

  const handleClear = () => {
    const currentFilter = JSON.parse(state.contractsFilter);
    if (currentFilter?.name !== "") {
      setSearchName("");
    }
    setSearchInputValue("");
  };

  // Sync search input value with filter state
  useEffect(() => {
    const currentFilter = JSON.parse(state.contractsFilter);
    if (currentFilter?.name !== searchInputValue) {
      setSearchInputValue(currentFilter?.name || "");
    }
  }, [state.contractsFilter]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInputValue !== JSON.parse(state.contractsFilter)?.name) {
        setSearchName(searchInputValue);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchInputValue]);
  //

  //
  useImperativeHandle(ref, () => ({
    refresh: () => {
      queryList?.refetch();
    },
  }));
  //

  //
  const startToolbarTemplate = (
    <div className="flex flex-wrap align-items-center justify-content-between gap-3 w-full">
      <div className="flex flex-wrap align-items-center gap-2">
        {showAddButton && <></>}
        <span className="p-input-icon-left mr-2 ">
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search"> </InputIcon>
            <InputText
              id="search"
              ref={searchInputRef}
              value={searchInputValue}
              onChange={(e) => setSearchInputValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Chercher un contrat"
            />
          </IconField>
        </span>
        <MeaButton onClick={handleSearch} icon="pi pi-search" />
        {searchInputValue && (
          <MeaButton
            onClick={handleClear}
            icon="pi pi-times"
            outlined
            className="ml-2"
          />
        )}
      </div>
      <div className="flex align-items-center gap-2 flex-shrink-0">
        <Dropdown
          value={
            contractsFilterParsed.not_fully_paid === "true"
              ? "true"
              : contractsFilterParsed.not_fully_paid === "fully_paid"
              ? "fully_paid"
              : ""
          }
          options={PAYMENT_FILTER_OPTIONS}
          onChange={(e) => setFilter("not_fully_paid", e.value ?? "")}
          optionLabel="label"
          optionValue="value"
          placeholder="Filtrer par paiement"
          className="w-full md:w-15rem"
          inputId="contracts-payment-filter"
          aria-label="Filtrer les contrats par état de paiement"
        />
      </div>
    </div>
  );
  //
  return (
    <>

      <BlockUI blocked={queryList?.isLoading || isLoading}>
        <MeaDataTable
          query={queryList}
          serviceAPI={contractsAPI}
          schoolId={schoolId}
          trainingId={trainingId}
          names={{ pagination: "contracts", singular: "contrat", plural: "contrats" }}
          startToolbarTemplate={startToolbarTemplate}
          showFilterButton={false}
          showToolbar={showToolbar}
          showDeleteButton={true}
          setPage={setPage}
          setPageSize={setPageSize}
          paginatorPosition="both"
        >
          <Column
            selectionMode="multiple"
            headerStyle={{ width: "3rem" }}
          ></Column>
          {selectedData.name && (
            <Column
              field="name"
              header="Nom du contrat"
              body={(item) => (
                <MeaLink
                  to={
                    getContractLink
                      ? getContractLink(item, { schoolId, trainingId })
                      : "/school/" + schoolId + "/admin/registrations/" + trainingId + "/contracts/" + item.id
                  }
                  permission={PERMISSION_TRAINING_CONTRACTS.GET}
                >
                  <strong>{item.name}</strong>
                </MeaLink>
              )}
            ></Column>
          )}
          {selectedData.id && (
            <Column
              field="id"
              header="N° de contrat"
              body={(item) => (
                <MeaLink
                  to={
                    getContractLink
                      ? getContractLink(item, { schoolId, trainingId })
                      : "/school/" + schoolId + "/admin/registrations/" + trainingId + "/contracts/" + item.id
                  }
                  permission={PERMISSION_TRAINING_CONTRACTS.GET}
                >
                  {item.id}
                </MeaLink>
              )}
            ></Column>
          )}
          {selectedData.students && (
            <Column
              field="students"
              header="Élèves"
              body={(item) => (
                <>
                  {item?.students?.map((student, index) => (
                    <MeaStudentAvatar key={index} student={student} permission={PERMISSION_TRAINING_STUDENTS.GET} schoolId={schoolId} trainingId={trainingId} link="candidates" />
                  ))}
                </>
              )}

            ></Column>
          )}
          {selectedData.parents && (
            <Column
              field="parents"
              header="Parents"
              body={(item) => (
                <>
                  {item?.parents?.map((parent, index) => (
                    <MeaMemberAvatar key={index} member={parent} permission={PERMISSION_SCHOOL_MEMBERS.GET} schoolId={schoolId} trainingId={trainingId} />
                  ))}
                </>
              )}

            ></Column>
          )}

          {selectedData.total_amount && (
            <Column
              field="total_amount"
              header="Total"
              body={(item) => (
                <>
                  <MeaContractTotalAmount contract={item} schoolId={schoolId} trainingId={trainingId} />
                </>
              )}

            ></Column>
          )}
          {selectedData.due_balance && (
            <Column
              field="due_balance"
              header="Reste à payer"
              body={(item) => (
                <>
                  <MeaContractDueBalance contract={item} schoolId={schoolId} trainingId={trainingId} />
                </>
              )}

            ></Column>
          )}
        </MeaDataTable>
      </BlockUI>

    </>
  );
});

export default ContractsListComponent;
